import { Clock3 } from "lucide-react";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import Webcam from "react-webcam";
import { API_BASE_URL } from "../../config";

// Define types
type Question = {
  question: string;
  options?: string[];
  marks: number;
  questionType: "multiple-choice" | "fillInBlank" | "fillInMultiple";
  correctAnswer: string | string[];
  image?: string;
  selectedAnswer?: string | string[] | null;
};

type QuestionsData = {
  [key: string]: Question[];
};

const getFillInBlankMarks = (
  selectedAnswer: string,
  correctAnswer: string | string[],
  isTwoAnswerQuestion: boolean
): number => {
  const selected = (selectedAnswer || "").trim().toLowerCase();
  if (!selected) return 0;

  const correctAnswersList = Array.isArray(correctAnswer)
    ? correctAnswer
    : [correctAnswer];

  if (isTwoAnswerQuestion) {
    const blank1Answers = new Set<string>();
    const blank2Answers = new Set<string>();

    correctAnswersList.forEach((ans) => {
      const splitParts = ans.split(/\s*,\s*|\s+and\s+|\s*&\s*|\s*\/\s*/i);
      if (splitParts.length > 1) {
        const p1 = splitParts[0].trim().toLowerCase();
        const p2 = splitParts[1].trim().toLowerCase();
        if (p1) blank1Answers.add(p1);
        if (p2) blank2Answers.add(p2);
      }
    });

    let matchedBlank1 = false;
    let matchedBlank2 = false;

    blank1Answers.forEach((b1) => {
      const regex = new RegExp(`\\b${b1}\\b`, "i");
      if (regex.test(selected) || selected === b1) {
        matchedBlank1 = true;
      }
    });

    blank2Answers.forEach((b2) => {
      const regex = new RegExp(`\\b${b2}\\b`, "i");
      if (regex.test(selected) || selected === b2) {
        matchedBlank2 = true;
      }
    });

    if (matchedBlank1 && matchedBlank2) {
      return 1;
    } else if (matchedBlank1 || matchedBlank2) {
      return 0.5;
    }
    return 0;
  } else {
    // Standard single answer
    const isCorrect = correctAnswersList.some(
      (correct) => correct.trim().toLowerCase() === selected
    );
    return isCorrect ? 1 : 0;
  }
};

const questionsData: QuestionsData = {
  memorizationPart_A: [
    {
      question:
        "1) Read these words carefully for 1 minute, then write down as many as you can remember after 5 minutes.",
      image:
        "https://res.cloudinary.com/dureqzdco/image/upload/v1742670810/Final_image__page-00012_cq0z7f.jpg",
      marks: 1,
      correctAnswer: [
        "river",
        "clock",
        "doctor",
        "bicycle",
        "mountain",
        "paper",
        "teacher",
        "glass",
        "hospital",
        "airplane",
        "elephant",
        "mirror",
        "school",
        "mango",
        "computer",
      ],
      questionType: "fillInMultiple",
      selectedAnswer: null,
    },
  ],
  memorizationPart_B: [
    {
      question: "1) The plural of 'tooth' is _________.",
      options: [],
      marks: 1,
      questionType: "fillInBlank",
      correctAnswer: ["teeth"],
      selectedAnswer: null,
    },
    {
      question: "2) A young dog is called a _________.",
      options: [],
      marks: 1,
      questionType: "fillInBlank",
      correctAnswer: [
        "puppies",
        "puppy",
        "a puppy",
        "a puppies",
        "the puppy",
        "the puppies",
        "puppy, puppies",
        "puppy,puppies",
        "puppy/puppies",
        "puppies/puppy",
        "puppy or puppies",
      ],
      selectedAnswer: null,
    },
    {
      question: "3) A person who cooks food is called a _________.",
      options: [],
      marks: 1,
      questionType: "fillInBlank",
      correctAnswer: [
        "cook",
        "chef",
        "a cook",
        "a chef",
        "the cook",
        "the chef",
        "cook, chef",
        "cook,chef",
        "cook/chef",
        "chef/cook",
        "cook or chef",
        "chef or cook",
      ],
      selectedAnswer: null,
    },
    {
      question: "4) The shape with four equal sides is called a _________.",
      options: [],
      marks: 1,
      questionType: "fillInBlank",
      correctAnswer: ["square", "a square", "the square"],
      selectedAnswer: null,
    },
    {
      question: "5) The opposite of 'fast' is _________.",
      options: [],
      marks: 1,
      questionType: "fillInBlank",
      correctAnswer: ["slow"],
      selectedAnswer: null,
    },
    {
      question: "6) A person who treats sick people is called a _________.",
      options: [],
      marks: 1,
      questionType: "fillInBlank",
      correctAnswer: [
        "doctor",
        "doctors",
        "a doctor",
        "a doctors",
        "the doctor",
        "the doctors",
        "doctor, doctors",
        "doctor,doctors",
        "doctor/doctors",
        "doctor or doctors",
      ],
      selectedAnswer: null,
    },
    {
      question: "7) The opposite of 'hot' is _________.",
      options: [],
      marks: 1,
      questionType: "fillInBlank",
      correctAnswer: ["cold"],
      selectedAnswer: null,
    },
    {
      question: "8) A baby cat is called a _________.",
      options: [],
      marks: 1,
      questionType: "fillInBlank",
      correctAnswer: [
        "kittens",
        "kitten",
        "a kitten",
        "a kittens",
        "the kitten",
        "the kittens",
        "kitten, kittens",
        "kitten,kittens",
        "kitten/kittens",
        "kitten or kittens",
      ],
      selectedAnswer: null,
    },
    {
      question: "9) The shape with three sides is called a _________.",
      options: [],
      marks: 1,
      questionType: "fillInBlank",
      correctAnswer: ["triangle", "a triangle", "the triangle"],
      selectedAnswer: null,
    },
    {
      question: "10) The plural of 'child' is _________.",
      options: [],
      marks: 1,
      questionType: "fillInBlank",
      correctAnswer: ["children"],
      selectedAnswer: null,
    },
  ],
  memorizationPart_C: [
    {
      question: "1) Library → ____",
      options: [
        "A tool to measure temperature",
        "A chart showing months and days",
        "A person who travels to space",
        "A tool to find directions",
        "A place to borrow books",
      ],
      marks: 1,
      questionType: "multiple-choice",
      correctAnswer: "a place to borrow books",
      selectedAnswer: null,
    },
    {
      question: "2) Thermometer → ____",
      options: [
        "A tool to measure temperature",
        "A chart showing months and days",
        "A person who travels to space",
        "A tool to find directions",
        "A place to borrow books",
      ],
      marks: 1,
      questionType: "multiple-choice",
      correctAnswer: "a tool to measure temperature",
      selectedAnswer: null,
    },
    {
      question: "3) Compass → ____",
      options: [
        "A tool to measure temperature",
        "A chart showing months and days",
        "A person who travels to space",
        "A tool to find directions",
        "A place to borrow books",
      ],
      marks: 1,
      questionType: "multiple-choice",
      correctAnswer: "a tool to find directions",
      selectedAnswer: null,
    },
    {
      question: "4) Calendar → ____",
      options: [
        "A tool to measure temperature",
        "A chart showing months and days",
        "A person who travels to space",
        "A tool to find directions",
        "A place to borrow books",
      ],
      marks: 1,
      questionType: "multiple-choice",
      correctAnswer: "a chart showing months and days",
      selectedAnswer: null,
    },
    {
      question: "5) Astronaut → ____",
      options: [
        "A tool to measure temperature",
        "A chart showing months and days",
        "A person who travels to space",
        "A tool to find directions",
        "A place to borrow books",
      ],
      marks: 1,
      questionType: "multiple-choice",
      correctAnswer: "a person who travels to space",
      selectedAnswer: null,
    },
  ],
  memorizationPart_D: [
    {
      question: "1) Who were the two colleagues in the story?",
      options: [
        "Riya and Ananya",
        "Riya and Meera",
        "Rohan and Aryan",
        "Ananya and Ravi",
      ],
      marks: 1,
      questionType: "multiple-choice",
      correctAnswer: "riya and ananya",
      selectedAnswer: null,
    },
    {
      question: "2) Where did they work?",
      options: [
        "At a school",
        "At a hospital",
        "At a supermarket",
        "At a restaurant",
      ],
      marks: 1,
      questionType: "multiple-choice",
      correctAnswer: "at a hospital",
      selectedAnswer: null,
    },
    {
      question: "3) What difficulty did the patient have?",
      options: [
        "They had trouble hearing",
        "They had difficulty walking",
        "They were unable to see",
        "They had a fever",
      ],
      marks: 1,
      questionType: "multiple-choice",
      correctAnswer: "they had difficulty walking",
      selectedAnswer: null,
    },
    {
      question: "4) How did Riya help the patient?",
      options: [
        "She brought them food",
        "She used a wheelchair to help them move",
        "She gave them extra medicine",
        "She called their family",
      ],
      marks: 1,
      questionType: "multiple-choice",
      correctAnswer: "she used a wheelchair to help them move",
      selectedAnswer: null,
    },
    {
      question: "5) What did Riya and Ananya do after their shift?",
      options: [
        "Went shopping",
        "Took a break and had tea",
        "Went home without talking",
        "Started another shift immediately",
      ],
      marks: 1,
      questionType: "multiple-choice",
      correctAnswer: "took a break and had tea",
      selectedAnswer: null,
    },
  ],
};

const MemorizationTest = () => {
  const [userAnswers, setUserAnswers] = useState<{ [key: string]: string }>({});
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningCount, setWarningCount] = useState(() => {
    // Get existing warning count from sessionStorage or start from 0
    const existingCount = sessionStorage.getItem("warningCount");
    return existingCount ? parseInt(existingCount, 10) : 0;
  });
  const webcamRef = React.useRef<Webcam>(null);

  // Function to update warning count on server
  const updateWarningCount = async (count: number) => {
    try {
      const warningId = sessionStorage.getItem("warningId");
      if (!warningId) {
        console.warn("Warning ID not found in session");
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/warning/update/${warningId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            warningCount: count.toString(),
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update warning count: ${response.status}`);
      }

      console.log(`Warning count updated to ${count}`);
    } catch (error) {
      console.error("Error updating warning count:", error);
    }
  };

  const lastWarningTimeRef = React.useRef<number>(0);

  // Function to handle warning
  const handleWarning = useCallback(() => {
    const now = Date.now();
    if (now - lastWarningTimeRef.current < 3000) {
      return;
    }
    lastWarningTimeRef.current = now;

    setWarningCount((prev) => {
      const newCount = prev + 1;
      // Update warning count on server
      updateWarningCount(newCount);
      // Save warning count to sessionStorage for continuity across pages
      sessionStorage.setItem("warningCount", newCount.toString());
      return newCount;
    });
    setShowWarningModal(true);
  }, []);

  // Get remaining time from previous page and add it to initial time
  const previousTimeStr = localStorage.getItem("remainingTime");
  const previousTime = previousTimeStr ? parseInt(previousTimeStr, 10) : 0;
  const [timeLeft, setTimeLeft] = useState(13 * 60 + previousTime); // 13 minutes + remaining time

  const [showImage, setShowImage] = useState(false);
  const [imageTimeLeft, setImageTimeLeft] = useState(60);
  const [startedImageView, setStartedImageView] = useState(false);

  // Add check for questionId
  useEffect(() => {
    const questionId = localStorage.getItem("questionId");

    // Prevent going back
    window.history.pushState(null, "", window.location.href);
    window.onpopstate = function () {
      window.history.pushState(null, "", window.location.href);
    };

    // Handle page reload/close
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Prevent tab switching
    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleWarning();
        // Force focus back to the test window
        window.focus();
      }
    };

    const handleBlur = () => {
      handleWarning();
      // Force focus back to the test window
      window.focus();
    };

    // Add event listeners for tab switching prevention
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    if (!questionId) {
      navigate("/registration");
      return;
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
    };
  }, [navigate, handleWarning]);

  // Clear the remaining time from localStorage after using it
  useEffect(() => {
    localStorage.removeItem("remainingTime");
  }, []);

  // Add auto-submit function
  const handleAutoSubmit = useCallback(async () => {
    setIsSubmitting(true);

    // Save remaining time for next page
    localStorage.setItem("remainingTime", timeLeft.toString());

    // Prepare data with selected answers
    const updatedData = { ...questionsData };
    Object.keys(updatedData).forEach((section) => {
      updatedData[section].forEach((question, index) => {
        if (section === "memorizationPart_A") {
          // Handle Part A scoring - check all 15 answers
          let totalMarks = 0;
          const answers: string[] = [];

          // Collect all answers from the 15 input fields
          for (let i = 0; i < 15; i++) {
            const answer = userAnswers[`${section}_${index}_${i}`];
            if (answer) answers.push(answer.trim());
          }

          // Compare with correct answers and calculate marks
          const correctAnswers = question.correctAnswer as string[];
          answers.forEach((answer) => {
            if (
              correctAnswers.some(
                (correct) => correct.toLowerCase() === answer.toLowerCase()
              )
            ) {
              totalMarks += 1;
            }
          });

          question.marks = totalMarks;
          question.selectedAnswer = answers;
        } else {
          // Handle other sections
          const selectedAnswer = userAnswers[`${section}_${index}`];
          if (selectedAnswer) {
            if (question.questionType === "fillInBlank") {
              const questionText = question.question || "";
              const isTwoAnswerQuestion = (questionText.match(/__+/g) || []).length >= 2;
              question.marks = getFillInBlankMarks(selectedAnswer, question.correctAnswer, isTwoAnswerQuestion);
            } else {
              // For multiple choice questions
              const correctStr = Array.isArray(question.correctAnswer)
                ? question.correctAnswer[0]
                : question.correctAnswer;
              question.marks =
                correctStr.toLowerCase() === selectedAnswer.trim().toLowerCase()
                  ? 1
                  : 0;
            }
            question.selectedAnswer = selectedAnswer;
          } else {
            question.marks = 0;
            question.selectedAnswer = null;
          }
        }
      });
    });

    // Send the updated data using fetch
    try {
      const questionId = localStorage.getItem("questionId");
      const response = await fetch(
        `${API_BASE_URL}/screening-questions/${questionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedData),
        }
      );

      if (response.ok) {
        console.log("Answers submitted successfully.");
        navigate("/aptitude");
      } else {
        throw new Error("Failed to submit answers.");
      }
    } catch (error) {
      console.error("Error submitting answers:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [timeLeft, userAnswers, navigate]);

  // Image timer effect
  useEffect(() => {
    if (showImage && imageTimeLeft > 0) {
      const timer = setTimeout(() => {
        setImageTimeLeft(imageTimeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (imageTimeLeft === 0) {
      setShowImage(false);
    }
  }, [imageTimeLeft, showImage]);

  // Timer effect
  useEffect(() => {
    if (timeLeft > 0 && !submitted) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !submitted) {
      setSubmitted(true);
      handleAutoSubmit();
    }
  }, [timeLeft, submitted, handleAutoSubmit]);

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    setUserAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleStartImageView = () => {
    setShowImage(true);
    setStartedImageView(true);
  };

  const handleSubmit = () => {
    setShowModal(true);
  };

  const confirmSubmit = async () => {
    setIsSubmitting(true);
    setSubmitted(true);

    // Save remaining time for next page
    localStorage.setItem("remainingTime", timeLeft.toString());

    // Prepare data with selected answers
    const updatedData = { ...questionsData };
    Object.keys(updatedData).forEach((section) => {
      updatedData[section].forEach((question, index) => {
        if (section === "memorizationPart_A") {
          // Handle Part A scoring - check all 15 answers
          let totalMarks = 0;
          const answers: string[] = [];

          // Collect all answers from the 15 input fields
          for (let i = 0; i < 15; i++) {
            const answer = userAnswers[`${section}_${index}_${i}`];
            if (answer) answers.push(answer.trim());
          }

          // Compare with correct answers and calculate marks
          const correctAnswers = question.correctAnswer as string[];
          answers.forEach((answer) => {
            if (
              correctAnswers.some(
                (correct) => correct.toLowerCase() === answer.toLowerCase()
              )
            ) {
              totalMarks += 1;
            }
          });

          question.marks = totalMarks;
          question.selectedAnswer = answers;
        } else {
          // Handle other sections
          const selectedAnswer = userAnswers[`${section}_${index}`];
          if (selectedAnswer) {
            if (question.questionType === "fillInBlank") {
              const questionText = question.question || "";
              const isTwoAnswerQuestion = (questionText.match(/__+/g) || []).length >= 2;
              question.marks = getFillInBlankMarks(selectedAnswer, question.correctAnswer, isTwoAnswerQuestion);
            } else {
              // For multiple choice questions
              const correctStr = Array.isArray(question.correctAnswer)
                ? question.correctAnswer[0]
                : question.correctAnswer;
              question.marks =
                correctStr.toLowerCase() === selectedAnswer.trim().toLowerCase()
                  ? 1
                  : 0;
            }
            question.selectedAnswer = selectedAnswer;
          } else {
            question.marks = 0;
            question.selectedAnswer = null;
          }
        }
      });
    });

    // Send the updated data using fetch
    try {
      const questionId = localStorage.getItem("questionId");
      const response = await fetch(
        `${API_BASE_URL}/screening-questions/${questionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedData),
        }
      );

      if (response.ok) {
        console.log("Answers submitted successfully.");
        navigate("/aptitude");
      } else {
        throw new Error("Failed to submit answers.");
      }
    } catch (error) {
      console.error("Error submitting answers:", error);
      alert("Submission failed. Please check your internet connection and try clicking Next again.");
      setSubmitted(false);
    } finally {
      setIsSubmitting(false);
      setShowModal(false);
    }
  };

  const renderMultipleChoiceQuestion = (
    question: Question,
    index: number,
    section: string
  ) => {
    const questionId = `${section}_${index}`;
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-4" key={questionId}>
        <div className="flex mb-4">
          <span className="font-semibold text-xl ml-2">
            {question.question}
          </span>
        </div>

        <div className="space-y-2">
          {question.options?.map((option, optIndex) => (
            <div
              key={optIndex}
              className={`border rounded-md p-3 hover:bg-gray-50 cursor-pointer transition-colors font-semibold text-lg ${
                userAnswers[questionId] === option
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200"
              }`}
              onClick={() => handleAnswerChange(questionId, option)}
            >
              <div className="flex items-center">
                <div
                  className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center border ${
                    userAnswers[questionId] === option
                      ? "border-blue-500"
                      : "border-gray-300"
                  }`}
                >
                  {userAnswers[questionId] === option && (
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  )}
                </div>
                <label className="w-full cursor-pointer">{option}</label>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderFillInBlankQuestion = (
    question: Question,
    index: number,
    section: string
  ) => {
    const questionId = `${section}_${index}`;
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-4" key={questionId}>
        <div className="flex mb-4">
          <span className="font-semibold text-xl ml-2">
            {question.question}
          </span>
        </div>

        <div className="w-full">
          <input
            type="text"
            placeholder="Enter your answer..."
            value={userAnswers[questionId] || ""}
            onChange={(e) => handleAnswerChange(questionId, e.target.value)}
            className="w-full border border-gray-300 rounded-md p-2 focus:border-blue-500 focus:ring-blue-500 font-semibold text-lg"
            disabled={submitted}
          />
        </div>
      </div>
    );
  };

  const renderFillInMultipleQuestion = (
    question: Question,
    index: number,
    section: string
  ) => {
    const questionId = `${section}_${index}`;
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-4" key={questionId}>
        <div className="flex mb-4">
          <span className="font-semibold text-xl ml-2">
            {question.question}
          </span>
        </div>

        {!startedImageView ? (
          <div className="flex justify-center mb-4">
            <button
              onClick={handleStartImageView}
              className="bg-gradient-to-r from-red-500 to-pink-600 text-white px-6 py-3 rounded-xl font-bold hover:scale-[1.02] transition-all duration-300"
            >
              View Words
            </button>
          </div>
        ) : question.image && showImage ? (
          <div className="flex flex-col items-center mb-4">
            <div className="mb-4 text-xl font-bold text-red-600">
              Time Remaining: {imageTimeLeft} seconds
            </div>
            <img
              src={question.image}
              alt="Question"
              className="max-w-full h-auto rounded-lg"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {Array.from({ length: 15 }, (_, i) => (
              <input
                key={i}
                type="text"
                placeholder={`Answer ${i + 1}`}
                value={userAnswers[`${questionId}_${i}`] || ""}
                onChange={(e) =>
                  handleAnswerChange(`${questionId}_${i}`, e.target.value)
                }
                className="w-full border border-gray-300 rounded-md p-2 focus:border-blue-500 focus:ring-blue-500 font-semibold text-lg"
                disabled={submitted}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render all sections with their headers
  const renderSection = (
    sectionName: string,
    questions: Question[],
    title: string,
    subtitle: string,
    explanation?: string
  ) => {
    return (
      <div className="mb-8 bg-white/80 border border-red-100 p-8 rounded-3xl shadow-xl">
        <div className="mb-8">
          <h2 className="bg-clip-text bg-gradient-to-r text-3xl text-center text-transparent font-bold from-red-600 to-pink-600 mb-3">
            {title}
          </h2>
          {subtitle && (
            <p className="text-black text-center text-lg font-medium mb-2">
              {subtitle}
            </p>
          )}
          {explanation && (
            <div className="mt-4 bg-red-50 border border-red-100 rounded-lg p-4">
              <p className="text-black text-md font-medium leading-relaxed italic">
                {explanation}
              </p>
            </div>
          )}
        </div>
        <div className="bg-gradient-to-r h-px w-full from-red-100 mb-8 to-pink-100"></div>
        {questions.map((question, index) => {
          return question.questionType === "multiple-choice"
            ? renderMultipleChoiceQuestion(question, index, sectionName)
            : question.questionType === "fillInBlank"
            ? renderFillInBlankQuestion(question, index, sectionName)
            : renderFillInMultipleQuestion(question, index, sectionName);
        })}
      </div>
    );
  };

  // Add this useEffect at the top of your other useEffects
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-200 justify-center items-center px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Webcam */}
        <div className="fixed bottom-5 right-5 z-20">
          <div className="bg-white/90 border-2 border-red-200 p-1 rounded-xl custom-shadow">
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                width: 150,
                height: 100,
                facingMode: "user",
              }}
              className="rounded-lg"
            />
          </div>
        </div>

        {/* Timer */}
        <div className="bg-white/90 border-2 border-red-200 p-3 rounded-xl  custom-shadow timer-container fixed top-5 right-5 z-20">
          <div className="flex gap-2 items-center">
            <div className="bg-gradient-to-r p-2 rounded-full from-red-500 to-pink-600">
              <Clock3 className="text-white " />
            </div>
            <div>
              <p className="text-gray-500 text-xs font-medium">
                Time Remaining
              </p>
              <p id="timerDisplay" className="text-gray-800 text-lg font-bold">
                {formatTime(timeLeft)}
              </p>
            </div>
          </div>
        </div>

        {/* Header Card */}
        <div
          className="bg-white p-8 rounded-3xl shadow-lg mb-10 overflow-hidden relative
        "
        >
          <div className="bg-red-50 h-40 rounded-full w-40 -mr-12 -mt-12 absolute opacity-70 right-0 top-0"></div>
          <div className="bg-red-50 h-32 rounded-full w-32 -mb-8 -ml-8 absolute bottom-0 left-0 opacity-70"></div>
          <div className="relative z-10">
            <h1 className="bg-clip-text bg-gradient-to-r text-4xl text-transparent font-bold from-red-600 mb-5 to-pink-600">
              Memorization Ability
            </h1>
            <div className="bg-gradient-to-r h-1.5 rounded-full w-24 from-red-500 mb-6 to-pink-500"></div>
          </div>
        </div>

        {/* Questions Container */}
        <form id="assessmentForm" className="space-y-16" autoComplete="off">
          {renderSection(
            "memorizationPart_A",
            questionsData.memorizationPart_A,
            "Part A: Word Recall Challenge",
            "(Fill in the blanks)"
          )}

          {renderSection(
            "memorizationPart_B",
            questionsData.memorizationPart_B,
            "Part B: Vocabulary Memorization",
            "(Fill in the blanks)"
          )}

          {renderSection(
            "memorizationPart_C",
            questionsData.memorizationPart_C,
            "Part C: Memory Matching Game",
            "(Multiple choice)"
          )}

          {renderSection(
            "memorizationPart_D",
            questionsData.memorizationPart_D,
            "Part D: Story Memory Test",
            "Read the following story aloud, then answer without looking.",
            "Riya and her colleague, Ananya, worked together in the hospital. One day, they were assigned to take care of a new patient who had difficulty walking. Riya helped the patient move around using a wheelchair, while Ananya assisted with the medication. The patient was grateful and thanked them for their kindness. After their shift, Riya and Ananya enjoyed a cup of tea before heading home."
          )}
        </form>

        {/* Submit Button */}
        <div className="flex justify-center mb-8 mt-12">
          <button
            onClick={handleSubmit}
            className="bg-gradient-to-r rounded-xl shadow-lg text-white w-1/2 duration-300 focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-red-500 font-bold from-red-500 hover:scale-[1.02] hover:shadow-2xl px-4 py-3 to-pink-600 transform transition-all flex justify-center items-center"
            disabled={submitted || isSubmitting}
          >
            <span>Next</span>
          </button>
        </div>

        {/* Confirmation Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Confirm Submission
              </h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to submit and proceed to the next section?
                You won't be able to return to this section.
              </p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSubmit}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg flex items-center justify-center min-w-[100px]"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <ThreeDots
                      height="24"
                      width="50"
                      color="#ffffff"
                      ariaLabel="loading"
                      wrapperClass="flex justify-center items-center"
                    />
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Warning Modal */}
        {showWarningModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
              <h2 className="text-2xl font-bold text-red-600 mb-4">
                Warning: Tab Switching Detected
              </h2>
              <p className="text-gray-600 mb-2">
                Please do not switch tabs or windows during the test. This may
                result in test termination.
              </p>
              <p className="text-red-500 font-semibold mb-6">
                Warning Count: {warningCount}
              </p>
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowWarningModal(false);
                    window.focus();
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg"
                >
                  I Understand
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemorizationTest;
